import {
    createSelector,
} from '@reduxjs/toolkit';

import pick from 'lodash/pick';

import { selectUserSelections } from '~Betslip/selectors/baseBetslipSelectors';
import { selectCombinationToSelectionIdsObject } from '~Betslip/selectors/combinationsSelectors';

import { isCombinationSelection } from '~Betslip/utils/combinationsUtils';
import { selectHasForceEachWay } from '~Betslip/selectors/selectionDetailsSelectors';

const PARLAY_TAB_MIN_SELECTIONS = 2;

const FETCH_FIELDS_ONLY = [
    'id', 'is_scorecast', 'is_cast_market', 'is_any',
];

export const selectUserSelectionIds = createSelector(
    selectUserSelections,
    selections => selections.map(({ id }) => id),
);

const selectEmptyStakeSelectionIds = createSelector(
    selectUserSelections,
    selections => selections.reduce(
        (acc, { stake, id, free_bet_id }) => {
            return (Number(stake) === 0 && !free_bet_id) ? [...acc, id] : acc;
        },
        [],
    ),
);

export const selectEmptyStakeSelections = createSelector(
    selectEmptyStakeSelectionIds,
    selectUserSelections,
    selectCombinationToSelectionIdsObject,
    (allEmptySingles, selections, combinationsAndSelectionIds) => {
        // track the selection ids that were bet on as part of a combination selection
        const combinationsSelectionsBetOn = selections.reduce(
            (acc, selection) => {
                if (isCombinationSelection(selection) && selection.stake) {

                    return acc.concat(combinationsAndSelectionIds[selection.id]);
                }

                return acc;
            },
            [],
        );

        // if a combination selection has all underlying selections bet on, then we do not retain the underlying selections
        return allEmptySingles.reduce(
            (acc, singleId) => {
                const currentSelection = selections.find(selection => selection.id === singleId);
                if (
                    currentSelection &&
                    !isCombinationSelection(currentSelection) &&
                    !combinationsSelectionsBetOn.includes(Number(singleId))
                ) {
                    const {
                        is_cast_market,
                        is_any,
                        is_scorecast,
                        combination_type,
                    } = currentSelection;

                    acc.emptySingles.push(singleId);
                    acc.emptySinglesFlags.push({
                        id: singleId,
                        is_cast_market,
                        is_any,
                        is_scorecast,
                        combination_type,
                    });
                }

                return acc;
            },
            {
                emptySingles: [],
                emptySinglesFlags: [],

            },
        );
    }
);

export const selectAnySelectionIsCastMarket = createSelector(
    [
        selectUserSelections,
    ],
    (userSelections) => {
        return Object.values(userSelections).some(userSelection => {
            return userSelection.is_cast_market;
        });
    }
);

export const selectAnySelectionReverseCast = createSelector(
    [
        selectUserSelections,
    ],
    (userSelections) => {
        return Object.values(userSelections).some(userSelection => {
            return userSelection.is_any;
        });
    }
);

export const selectUserSelectionsDataForFetch = createSelector(
    selectUserSelections,
    selections => selections.reduce(
        (acc, curr) => (
            !isCombinationSelection(curr)
                ? [...acc, pick(curr, FETCH_FIELDS_ONLY)]
                : acc
        ),
        [],
    )
);

/**
 * Generally you want selections without combinations. Combination selections are a
 * "meta-selection" and cannot exist in the betslip without their underlying seelections. They do
 * not count towards betslip selection counts for example (business logic)
 */
export const selectUserSelectionsWithoutCombinations = createSelector(
    selectUserSelections,
    selections => selections.filter(selection => !isCombinationSelection(selection))
);

export const selectUserSelectionIdsWithoutCombinations = createSelector(
    selectUserSelectionsWithoutCombinations,
    selections => selections.map(({ id }) => id),
);

export const selectCountUserSelectionsWithoutCombinations = createSelector(
    selectUserSelectionsWithoutCombinations,
    selections => selections.length,
);

export const selectUserSelectionById = createSelector(
    [
        selectUserSelections,
        (_state, selectionId) => (selectionId),
    ],
    (userSelections, selectionId) => (userSelections.find(selection => selection.id === selectionId))
);

export const selectUserSelectionStakeById = createSelector(
    selectUserSelectionById,
    userSelections => userSelections?.stake,
);

export const selectUserSelectionOriginalPriceById = createSelector(
    selectUserSelectionById,
    userSelections => userSelections?.original_price,
);

export const selectIsParlayTabAvailable = createSelector(
    [
        selectUserSelections,
        selectHasForceEachWay,
    ],
    (userSelections, hasForceEachWay) => {
        return userSelections.length >= PARLAY_TAB_MIN_SELECTIONS && !hasForceEachWay;
    }
);

export const selectUserHasAnyCastSelection = createSelector(
    selectUserSelections,
    userSelections => userSelections.some(
        userSelection => userSelection.is_cast_market === true
            || userSelection.is_scorecast === true
    )
);

// Stake match intentionally ignores each_way_selected so that a forced
// each-way selection (which can differ from the others) does not blank out
// the shared "all singles" stake field while the user is typing.
const singlesStakeMatch = (currentUserSelection, idx, traversedUserSelection) => {
    const {
        stake: currentStake,
        free_bet_id: currentFbId,
    } = currentUserSelection;
    const [{
        stake: traversedStake,
        free_bet_id: traversedFbId,
    }] = traversedUserSelection;

    return currentStake === traversedStake
        && currentFbId === traversedFbId;
};

const selectSinglesStakeMatch = createSelector(
    selectUserSelections,
    userSelections => {
        if (userSelections.length < 1) {
            return false;
        }

        return userSelections.every(singlesStakeMatch);
    },
);

// EW match only checks each_way_selected, intentionally ignoring stake so the
// combined EW checkbox reflects the correct state even when stakes differ.
const selectSinglesEwMatch = createSelector(
    selectUserSelections,
    userSelections => {
        if (userSelections.length < 1) {
            return false;
        }

        return userSelections.every(
            selection => selection.each_way_selected === userSelections[0].each_way_selected
        );
    },
);

export const selectSinglesStake = createSelector(
    [
        selectUserSelections,
        selectSinglesStakeMatch,
    ],
    (userSelections, singlesStakeMatch) => {
        return singlesStakeMatch ? userSelections[0].stake : 0;
    }
);

export const selectSinglesEwSelected = createSelector(
    [
        selectUserSelections,
        selectSinglesEwMatch,
    ],
    (userSelections, singlesEwMatch) => {
        return singlesEwMatch ? userSelections[0].each_way_selected : false;
    }
);

export const selectHasAnySpSelected = createSelector(
    selectUserSelections,
    userSelections => {
        return Object.values(userSelections).some(userSelection => userSelection.sp_selected);
    },
);

export const selectCastMarketSelectionIds = createSelector(
    selectUserSelections,
    userSelections => userSelections
        .filter(({ is_cast_market }) => is_cast_market)
        .map(({ id }) => id.split('_')),
);

export const selectCastOrScorecastSelections = createSelector(
    selectUserSelections,
    userSelections => userSelections
        .filter(({ is_cast_market, is_scorecast }) => (is_cast_market || is_scorecast)),
);

export const selectIsSelectionInBetslip = createSelector(
    [
        selectUserSelections,
        (state, selectionId) => selectionId,
        (state, selectionId, isMultiBet) => isMultiBet,
    ],
    (userSelections, selectionId, isMultiBet) => {
        const ids = isMultiBet ? selectionId.split('_') : [selectionId];

        return ids.every(id => userSelections.find(selection => selection.id === id));
    }
);
